import { useState, useEffect } from 'react';
import { Badge, Button, Row, Col, Form, ProgressBar, Modal, ListGroup, InputGroup } from 'react-bootstrap';
import { useApi } from '../../../contexts/ApiProvider';

export default function TripPackingMatrix({ trip }) {
    const api = useApi();
    const [packingItems, setPackingItems] = useState([]);

    const [masterItems, setMasterItems] = useState([]);
    const [showImportModal, setShowImportModal] = useState(false);
    const [selectedImports, setSelectedImports] = useState([]);

    const [importSearch, setImportSearch] = useState('');
    const [importTravelerFilter, setImportTravelerFilter] = useState('All');

    // --- UPDATED: Added packed_in ---
    const [newAdHoc, setNewAdHoc] = useState({ name: '', category: 'Souvenir', qty: 1, traveler_name: 'Shared', packed_in: 'Unassigned' });
    const [showEditItem, setShowEditItem] = useState(false);
    const [editItemData, setEditItemData] = useState({ id: null, name: '', category: '', qty: 1, traveler_name: 'Shared', packed_in: 'Unassigned' });

    const categories = ['Clothing', 'Toiletries', 'Electronics', 'Documents', 'Health', 'Activity Gear', 'Snacks/Food', 'Misc', 'Souvenir'];
    const travelers = ['Shared', 'Deepak', 'Amy'];
    const bagTypes = ['Unassigned', 'Carry-On', 'Personal Item', 'Checked Bag', 'Daypack', 'Trunk'];

    useEffect(() => {
        fetchPackingItems();
        fetchMasterItems();
        // eslint-disable-next-line
    }, [trip.id]);

    const fetchPackingItems = async () => {
        const res = await api.get(`/travel/trips/${trip.id}/items`);
        if (res.ok) setPackingItems(res.body.items || []);
    };

    const fetchMasterItems = async () => {
        const res = await api.get('/travel/master-items?per_page=100');
        if (res.ok) {
            setMasterItems(res.body.items.filter(i => i.is_active !== false) || []);
        }
    };

    const toggleItemStatus = async (itemId, field, currentValue) => {
        setPackingItems(items => items.map(i => i.id === itemId ? { ...i, [field]: !currentValue } : i));
        await api.put(`/travel/items/${itemId}`, { [field]: !currentValue });
    };

    const handleAddAdHoc = async (e) => {
        e.preventDefault();
        const res = await api.post(`/travel/trips/${trip.id}/items`, {
            ...newAdHoc,
            qty: parseFloat(newAdHoc.qty) || 1
        });
        if (res.ok) {
            setNewAdHoc({ name: '', category: 'Souvenir', qty: 1, traveler_name: 'Shared', packed_in: 'Unassigned' });
            fetchPackingItems();
        }
    };

    const handleImportItems = async () => {
        const itemsToAdd = masterItems.filter(m => selectedImports.includes(m.id));

        await Promise.all(itemsToAdd.map(item =>
            api.post(`/travel/trips/${trip.id}/items`, {
                name: item.name,
                category: item.category,
                qty: item.default_qty || 1,
                traveler_name: item.traveler_name || 'Shared',
                packed_in: 'Unassigned' // Imported items start unassigned
            })
        ));

        setShowImportModal(false);
        setSelectedImports([]);
        setImportSearch('');
        setImportTravelerFilter('All');
        fetchPackingItems();
    };

    const handleToggleImportSelection = (id) => {
        setSelectedImports(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const handleUpdateItem = async (e) => {
        e.preventDefault();
        const res = await api.put(`/travel/items/${editItemData.id}`, editItemData);
        if (res.ok) { setShowEditItem(false); fetchPackingItems(); }
    };

    const handleDeleteItem = async (itemId) => {
        if (!window.confirm("Remove this item?")) return;
        const res = await api.delete(`/travel/items/${itemId}`);
        if (res.ok) fetchPackingItems();
    };

    const groupedItems = categories.reduce((acc, cat) => {
        acc[cat] = packingItems.filter(item => item.category === cat);
        return acc;
    }, {});
    const otherItems = packingItems.filter(item => !categories.includes(item.category));
    if (otherItems.length > 0) groupedItems['Other'] = otherItems;

    const totalItems = packingItems.length;
    const packedOutbound = packingItems.filter(i => i.packing_status).length;
    const outboundProgress = totalItems === 0 ? 0 : Math.round((packedOutbound / totalItems) * 100);

    // --- FIX: The "Socks" Bug ---
    // Now we combine name + traveler so Amy's socks don't hide Deepak's socks!
    const packedKeys = packingItems.map(i => `${i.name.toLowerCase().trim()}|${i.traveler_name || 'Shared'}`);
    const missingMasterItems = masterItems.filter(m => {
        const key = `${m.name.toLowerCase().trim()}|${m.traveler_name || 'Shared'}`;
        return !packedKeys.includes(key);
    });

    const filteredMissingItems = missingMasterItems.filter(item => {
        const matchesSearch = item.name.toLowerCase().includes(importSearch.toLowerCase()) ||
                              item.category.toLowerCase().includes(importSearch.toLowerCase());
        const matchesTraveler = importTravelerFilter === 'All' || item.traveler_name === importTravelerFilter;
        return matchesSearch && matchesTraveler;
    });

    return (
        <div className="bg-white fade-in">
            <div className="p-4 border-bottom bg-light d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div style={{ flex: '1 1 300px' }}>
                    <div className="d-flex justify-content-between small fw-bold mb-1">
                        <span>Outbound Packing Progress</span>
                        <span className={outboundProgress === 100 ? 'text-success' : 'text-primary'}>{packedOutbound} / {totalItems} Items</span>
                    </div>
                    <ProgressBar variant={outboundProgress === 100 ? 'success' : 'primary'} now={outboundProgress} style={{ height: '8px' }} />
                </div>

                <div className="d-flex gap-3 align-items-center">
                    <Button variant="outline-primary" size="sm" className="fw-bold text-nowrap" onClick={() => setShowImportModal(true)}>
                        <i className="bi bi-box-arrow-in-down me-2"></i>Closet
                    </Button>

                    <Form onSubmit={handleAddAdHoc} className="d-flex flex-wrap gap-2 align-items-center mt-3 mt-md-0">
                        <Form.Control size="sm" type="text" placeholder="Ad-hoc item..." value={newAdHoc.name} onChange={e => setNewAdHoc({...newAdHoc, name: e.target.value})} required className="flex-grow-1" style={{ minWidth: '120px' }} />
                        <Form.Select size="sm" value={newAdHoc.category} onChange={e => setNewAdHoc({...newAdHoc, category: e.target.value})} style={{ width: 'auto' }}>
                            {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                        </Form.Select>
                        <Form.Select size="sm" value={newAdHoc.traveler_name} onChange={e => setNewAdHoc({...newAdHoc, traveler_name: e.target.value})} style={{ width: 'auto' }}>
                            {travelers.map(t => <option key={t} value={t}>{t}</option>)}
                        </Form.Select>
                        <Button type="submit" size="sm" variant="primary" className="fw-bold">Add</Button>
                    </Form>
                </div>
            </div>

            <div className="p-4">
                {totalItems === 0 ? <div className="text-center py-5 text-muted">No items assigned. Import from your closet above!</div> : (
                    Object.entries(groupedItems).map(([catName, catItems]) => {
                        if (catItems.length === 0) return null;
                        return (
                            <div key={catName} className="mb-4">
                                <h6 className="fw-bold text-uppercase letter-spacing-1 text-muted mb-2 border-bottom pb-2">{catName}</h6>
                                {catItems.map(item => (
                                    <div key={item.id} className="d-flex align-items-center justify-content-between py-2 border-bottom border-light hover-bg-light px-2 rounded">
                                        <div className="d-flex align-items-center gap-3">
                                            <Form.Check type="checkbox" id={`outbound-${item.id}`} checked={item.packing_status} onChange={() => toggleItemStatus(item.id, 'packing_status', item.packing_status)} className="fs-5" />
                                            <div>
                                                <span className={`fw-bold ${item.packing_status ? 'text-decoration-line-through text-muted' : ''}`}>{item.name}</span>
                                                {item.traveler_name && item.traveler_name !== 'Shared' && (
                                                    <Badge bg={item.traveler_name === 'Deepak' ? 'primary' : 'info'} className="ms-2 rounded-pill bg-opacity-75 text-white">
                                                        {item.traveler_name}
                                                    </Badge>
                                                )}
                                                {item.qty > 1 && <Badge bg="secondary" className="ms-2 rounded-pill opacity-75">Qty: {item.qty}</Badge>}

                                                {/* --- NEW: Display the Bag --- */}
                                                {item.packed_in && item.packed_in !== 'Unassigned' && (
                                                    <span className="small text-muted ms-2 fw-bold opacity-75">
                                                        <i className="bi bi-briefcase-fill me-1"></i>{item.packed_in}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-3">
                                            {['Active', 'Completed'].includes(trip.status) && (
                                                <div className="d-flex align-items-center gap-2 border-end pe-3 d-none d-md-flex">
                                                    <span className="small text-muted fw-bold">Return:</span>
                                                    <Form.Check type="checkbox" id={`return-${item.id}`} checked={item.return_packed_status} onChange={() => toggleItemStatus(item.id, 'return_packed_status', item.return_packed_status)} />
                                                </div>
                                            )}
                                            <Button variant="link" className="text-primary p-0" onClick={() => { setEditItemData(item); setShowEditItem(true); }}><i className="bi bi-pencil-square"></i></Button>
                                            <Button variant="link" className="text-danger p-0" onClick={() => handleDeleteItem(item.id)}><i className="bi bi-trash3"></i></Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })
                )}
            </div>

            <Modal show={showImportModal} onHide={() => {setShowImportModal(false); setSelectedImports([]); setImportSearch(''); setImportTravelerFilter('All');}} centered scrollable>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold">Import from Closet</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="mb-3 g-2">
                        <Col md={7}>
                            <InputGroup size="sm">
                                <InputGroup.Text className="bg-white"><i className="bi bi-search text-muted"></i></InputGroup.Text>
                                <Form.Control placeholder="Search closet..." value={importSearch} onChange={e => setImportSearch(e.target.value)} />
                            </InputGroup>
                        </Col>
                        <Col md={5}>
                            <Form.Select size="sm" value={importTravelerFilter} onChange={e => setImportTravelerFilter(e.target.value)}>
                                <option value="All">All Travelers</option>
                                {travelers.map(t => <option key={t} value={t}>{t}</option>)}
                            </Form.Select>
                        </Col>
                    </Row>

                    {missingMasterItems.length === 0 ? (
                        <div className="text-center py-4 text-muted bg-light rounded">
                            <i className="bi bi-check-circle display-6 d-block mb-2 text-success opacity-50"></i>
                            <p className="mb-0 fw-bold">All caught up!</p>
                            <small>Every active template is already packed.</small>
                        </div>
                    ) : filteredMissingItems.length === 0 ? (
                        <div className="text-center py-4 text-muted bg-light rounded">
                            <p className="mb-0">No items match your filters.</p>
                        </div>
                    ) : (
                        <ListGroup variant="flush" className="border rounded">
                            {filteredMissingItems.map(item => (
                                <ListGroup.Item key={item.id} action onClick={() => handleToggleImportSelection(item.id)} className="d-flex justify-content-between align-items-center py-3">
                                    <div>
                                        <div className="fw-bold text-dark">
                                            {item.name}
                                            {item.traveler_name && item.traveler_name !== 'Shared' && (
                                                <Badge bg={item.traveler_name === 'Deepak' ? 'primary' : 'info'} className="ms-2 rounded-pill bg-opacity-75 text-white">
                                                    {item.traveler_name}
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="small text-muted">{item.category} • Qty: {item.default_qty}</div>
                                    </div>
                                    <Form.Check type="checkbox" checked={selectedImports.includes(item.id)} onChange={() => {}} />
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 bg-light">
                    <Button variant="secondary" onClick={() => setShowImportModal(false)} className="rounded-pill fw-bold">Cancel</Button>
                    <Button variant="primary" onClick={handleImportItems} disabled={selectedImports.length === 0} className="rounded-pill fw-bold">
                        Import {selectedImports.length > 0 ? `(${selectedImports.length})` : ''} Items
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showEditItem} onHide={() => setShowEditItem(false)} centered fullscreen="md-down">
                <Modal.Header closeButton className="border-0 pb-0"><Modal.Title className="fw-bold">Edit Item</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleUpdateItem}>
                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-bold text-muted mb-1">Item Name</Form.Label>
                            <Form.Control type="text" value={editItemData.name} onChange={e => setEditItemData({...editItemData, name: e.target.value})} required />
                        </Form.Group>
                        <Row className="mb-4 g-2">
                            <Col md={6}>
                                <Form.Label className="small fw-bold text-muted mb-1">Category</Form.Label>
                                <Form.Select value={editItemData.category} onChange={e => setEditItemData({...editItemData, category: e.target.value})}>
                                    {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                    {editItemData.category && !categories.includes(editItemData.category) && <option value={editItemData.category}>{editItemData.category}</option>}
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Form.Label className="small fw-bold text-muted mb-1">Assigned To</Form.Label>
                                <Form.Select value={editItemData.traveler_name || 'Shared'} onChange={e => setEditItemData({...editItemData, traveler_name: e.target.value})}>
                                    {travelers.map(t => <option key={t} value={t}>{t}</option>)}
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Form.Label className="small fw-bold text-muted mb-1">Quantity</Form.Label>
                                <Form.Control type="number" step="0.5" value={editItemData.qty} onChange={e => setEditItemData({...editItemData, qty: e.target.value})} required />
                            </Col>
                        </Row>

                        {/* --- NEW: Bag Tracker Field --- */}
                        <Form.Group className="mb-4 pt-3 border-top">
                            <Form.Label className="small fw-bold text-primary mb-1"><i className="bi bi-briefcase-fill me-1"></i>Packed In (Bag Location)</Form.Label>
                            <Form.Select value={editItemData.packed_in || 'Unassigned'} onChange={e => setEditItemData({...editItemData, packed_in: e.target.value})}>
                                {bagTypes.map(bag => <option key={bag} value={bag}>{bag}</option>)}
                                {editItemData.packed_in && !bagTypes.includes(editItemData.packed_in) && <option value={editItemData.packed_in}>{editItemData.packed_in}</option>}
                            </Form.Select>
                            <Form.Text className="text-muted">Update this during your trip if you move items between bags.</Form.Text>
                        </Form.Group>

                        <Button variant="primary" type="submit" className="w-100 fw-bold rounded-pill">Save Changes</Button>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
}
